<script lang="ts">
import { computed, defineComponent, reactive, watch } from 'vue';
import ItemGroup from '@/src/components/ItemGroup.vue';
import GroupableItem from '@/src/components/GroupableItem.vue';
import ImageListCard from '@/src/components/ImageListCard.vue';
import { createVTKImageThumbnailer } from '@/src/core/thumbnailers/vtk-image';
import { useSegmentGroupStore } from '@/src/store/segmentGroups';
import {
  isRegularImage,
  type DataSelection,
  selectionEquals,
} from '@/src/utils/dataSelection';
import { useImageCacheStore } from '@/src/store/image-cache';
import { defaultImageMetadata } from '@/src/core/progressiveImage';
import { useImageStore } from '../store/datasets-images';
import { useDatasetStore } from '../store/datasets';

import { useMultiSelection } from '../composables/useMultiSelection';
import { useLayersStore } from '../store/datasets-layers';
import { useViewSliceStore } from '../store/view-configs/slicing';
import { useViewCameraStore } from '../store/view-configs/camera';
import { useI18n } from 'vue-i18n';

function imageCacheKey(dataID: string) {
  return `image-${dataID}`;
}

export default defineComponent({
  components: {
    ItemGroup,
    GroupableItem,
    ImageListCard,
  },
  setup() {
    const { t } = useI18n();
    const imageStore = useImageStore();
    const dataStore = useDatasetStore();
    const layersStore = useLayersStore();
    const segmentGroupStore = useSegmentGroupStore();
    const viewSliceStore = useViewSliceStore();
    const viewCameraStore = useViewCameraStore();
    const imageCacheStore = useImageCacheStore();

    const primarySelection = computed(() => dataStore.primarySelection);

    const nonDICOMImages = computed(() => imageStore.idList);

    const images = computed(() => {
      const layerImages = layersStore
        .getLayers(primarySelection.value)
        .filter(({ selection }) => isRegularImage(selection));
      const layerImageIDs = layerImages.map(({ selection }) => selection);
      const loadedLayerImageIDs = layerImages
        .filter(({ id }) => imageCacheStore.imageById[id]?.isLoaded())
        .map(({ selection }) => selection);

      const selectedImageID =
        isRegularImage(primarySelection.value) && primarySelection.value;

      return nonDICOMImages.value.map((id) => {
        const selectionKey = id as DataSelection;
        const isLayer = layerImageIDs.includes(id);
        const layerLoaded = loadedLayerImageIDs.includes(id);
        const layerLoading = isLayer && !layerLoaded;
        const layerable =
          id !== selectedImageID && primarySelection.value != null;
        const metadata =
          imageCacheStore.getImageMetadata(id) ?? defaultImageMetadata();
        return {
          id,
          cacheKey: imageCacheKey(id),
          // for UI selection
          selectionKey,
          name: metadata.name,
          dimensions: metadata.dimensions,
          spacing: [...metadata.spacing].map((s) => s.toFixed(2)),
          layerable,
          layerLoading,
          isLayer,
          layerHandler: () => {
            if (!layerLoading && layerable) {
              if (isLayer)
                layersStore.deleteLayer(primarySelection.value, selectionKey);
              else layersStore.addLayer(primarySelection.value, selectionKey);
            }
          },
        };
      });
    });
    
    // --- thumbnails --- //

    type Thumbnail = {
      imageURI: string;
      aspectRatio: number;
    };
    const thumbnails = reactive<Record<string, Thumbnail>>({});
    const thumbnailer = createVTKImageThumbnailer();

    watch(
      nonDICOMImages,
      (imageIDs) => {
        imageIDs.forEach(async (id) => {
          const cacheKey = imageCacheKey(id);
          if (!(cacheKey in thumbnails)) {
            const imageData = imageCacheStore.getVtkImageData(id);
            if (!imageData) return;
            const canvasIM = thumbnailer.generate(imageData);
            const imageURI = thumbnailer.imageDataToDataURI(canvasIM, 100, 100);
            const dims = imageData.getDimensions();
            const aspectRatio = dims[0] / dims[1];
            thumbnails[cacheKey] = { imageURI, aspectRatio };
          }
        });

        // delete old thumbnails
        const idLookup = new Set(imageIDs.map((id) => imageCacheKey(id)));
        Object.keys(thumbnails).forEach((cacheKey) => {
          if (!idLookup.has(cacheKey)) {
            delete thumbnails[cacheKey];
          }
        });
      },
      { immediate: true, deep: true }
    );

    // --- sync --- //
    const sameSpaceImages = computed(() => {
      return imageStore.checkAllImagesSameSpace();
    });
    const isSync = computed(() => {
      return viewSliceStore.isSync() && viewCameraStore.isSync();
    });
    function toggleSyncImages() {
      viewSliceStore.toggleSyncImages();
      viewCameraStore.toggleSyncCameras();
      viewCameraStore.disableCameraAutoReset = isSync.value;
    }
    watch(isSync, () => {
      viewSliceStore.updateSyncConfigs();
      viewCameraStore.updateSyncConfigs();
    });

    // --- selection --- //

    const { selected, selectedAll, selectedSome, toggleSelectAll } =
      useMultiSelection(nonDICOMImages);

    function removeSelection() {
      selected.value.forEach(dataStore.remove);
      selected.value = [];
    }

    function convertToLabelMap(key: string) {
      if (primarySelection.value) {
        segmentGroupStore.convertImageToLabelmap(key, primarySelection.value);
      }
    }

    function removeData(id: string) {
      dataStore.remove(id);
    }

    return {
      t,
      selected,
      selectedAll,
      selectedSome,
      toggleSelectAll,
      removeSelection,
      removeData,
      convertToLabelMap,
      images,
      thumbnails,
      primarySelection,
      selectionEquals,
      setPrimarySelection: (sel: DataSelection) => {
        dataStore.setPrimarySelection(sel);
      },
      sameSpaceImages,
      toggleSyncImages,
      isSync,
    };
  },
});
</script>

<template>
  <div>
    <div v-if="images.length === 0" class="text-center">
      {{ t('imageDataBrowser.noImagesLoaded') }}
    </div>
    <item-group
      :model-value="primarySelection"
      :equals-test="selectionEquals"
      @update:model-value="setPrimarySelection"
    >
      <groupable-item
        v-for="image in images"
        :key="image.id"
        v-slot="{ active, select }"
        :value="image.selectionKey"
      >
        <image-list-card
          class="mt-1 position-relative"
          :active="active"
          :html-title="image.name"
          :image-url="(thumbnails[image.cacheKey] || {}).imageURI || ''"
          :image-size="100"
          @click="select"
        >
          <div class="d-flex flex-row justify-space-between">
            <div class="allow-trunc-text-flex-child">
              <div
                class="text-body-2 font-weight-bold text-no-wrap text-truncate"
              >
                {{ image.name }}
              </div>
              <div class="text-caption">
                {{ t('imageDataBrowser.dims') }}: ({{ image.dimensions.join(', ') }})
              </div>
              <div class="text-caption">
                {{ t('imageDataBrowser.spacing') }}: ({{ image.spacing.join(', ') }})
              </div>
            </div>
          </div>
        </image-list-card>
      </groupable-item>
    </item-group>
  </div>
</template>

<style scoped>
.allow-trunc-text-flex-child {
  min-width: 0;
}

.dataset-menu {
  position: relative;
  top: -8px;
  right: -4px;
}
</style>
